import React from "react";
import { Layout, Drawer } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { NavItem } from "../types";

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  isMobile: boolean;
  onDrawerClose: () => void;
}

const navItems: NavItem[] = [
  {
    key: "dashboard",
    icon: "DashboardOutlined",
    label: "Dashboard",
    path: "/",
  },
  {
    key: "consumer-applications",
    icon: "UserOutlined",
    label: "Consumer Applications",
    path: "/consumer-applications",
    badge: true,
  },
  {
    key: "shop-reimbursements",
    icon: "ShopOutlined",
    label: "Shop Reimbursements",
    path: "/shop-reimbursements",
    badge: true,
  },
  {
    key: "participating-shops",
    icon: "ShopOutlined",
    label: "Participating Shops",
    path: "/participating-shops",
  },
  {
    key: "reports",
    icon: "FileTextOutlined",
    label: "Reports",
    path: "/reports",
  },
  {
    key: "users",
    icon: "TeamOutlined",
    label: "Users",
    path: "/users",
  },
];

const supportItems: NavItem[] = [
  {
    key: "faqs",
    icon: "QuestionCircleOutlined",
    label: "FAQs",
    path: "/faqs",
  },
  {
    key: "contact",
    icon: "ContactsOutlined",
    label: "Contact Us",
    path: "/contact",
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  isMobile,
  onDrawerClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuClick = (key: string, path: string) => {
    navigate(path);
    if (isMobile) {
      onDrawerClose();
    }
  };

  const selectedKey = React.useMemo(() => {
    const currentPath = location.pathname;
    const navItem = navItems.find((item) => item.path === currentPath);
    const supportItem = supportItems.find((item) => item.path === currentPath);
    return navItem?.key || supportItem?.key || "dashboard";
  }, [location.pathname]);

  const sidebarContent = (
    <div
      style={{
        borderRight: "1px solid #CCC",
        display: "flex",
        width: "100%",
        padding: "25px 10px 208px 10px",
        flexDirection: "column",
        alignItems: "stretch",
        backgroundColor: "#FFF",
        minHeight: "100vh",
      }}
    >
      {/* Logo */}
      <img
        src="/images/logo.png"
        alt="New Orleans E-Bike Program Logo"
        style={{
          aspectRatio: "1",
          objectFit: "contain",
          objectPosition: "center",
          width: "130px",
          alignSelf: "center",
          maxWidth: "100%",
        }}
      />

      {/* Main Navigation */}
      <div
        style={{
          display: "flex",
          marginTop: "25px",
          alignItems: "start",
          gap: "10px",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            borderRadius: "4px",
            backgroundColor: "#FFF",
            minWidth: "240px",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#CCC",
              marginBottom: "10px",
            }}
          />

          {/* Dashboard */}
          <div
            onClick={() => handleMenuClick("dashboard", "/")}
            style={{
              borderRadius: "4px",
              backgroundColor:
                selectedKey === "dashboard"
                  ? "rgba(73, 121, 167, 0.1)"
                  : "#FFF",
              display: "flex",
              marginTop: "10px",
              minHeight: "44px",
              width: "100%",
              padding: "10px",
              alignItems: "center",
              gap: "10px",
              fontSize: "18px",
              color: selectedKey === "dashboard" ? "#4979A7" : "#4D4D4D",
              fontWeight: "400",
              letterSpacing: "0.5px",
              lineHeight: "1",
              justifyContent: "start",
              cursor: "pointer",
            }}
          >
            <img
              src="/images/dashboard-icon.svg"
              alt="Dashboard"
              style={{
                width: "24px",
                height: "24px",
                flexShrink: 0,
              }}
            />
            <div>Dashboard</div>
          </div>

          {/* Consumer Applications */}
          <div
            onClick={() =>
              handleMenuClick("consumer-applications", "/consumer-applications")
            }
            style={{
              alignItems: "center",
              borderRadius: "4px",
              display: "flex",
              marginTop: "10px",
              minHeight: "44px",
              width: "100%",
              padding: "10px",
              gap: "10px",
              justifyContent: "start",
              backgroundColor:
                selectedKey === "consumer-applications"
                  ? "rgba(73, 121, 167, 0.1)"
                  : "#FFF",
              fontSize: "18px",
              color:
                selectedKey === "consumer-applications" ? "#4979A7" : "#4D4D4D",
              fontWeight: "400",
              letterSpacing: "0.5px",
              lineHeight: "1",
              cursor: "pointer",
            }}
          >
            <img
              src="/images/consumerappln.png"
              alt="Consumer Applications"
              style={{
                width: "24px",
                height: "24px",
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1 }}>Consumer Applications</div>
          </div>

          {/* Shop Reimbursements */}
          <div
            onClick={() =>
              handleMenuClick("shop-reimbursements", "/shop-reimbursements")
            }
            style={{
              alignItems: "center",
              borderRadius: "4px",
              display: "flex",
              marginTop: "10px",
              width: "100%",
              padding: "10px",
              gap: "10px",
              justifyContent: "start",
              backgroundColor:
                selectedKey === "shop-reimbursements"
                  ? "rgba(73, 121, 167, 0.1)"
                  : "#FFF",
              fontSize: "18px",
              color:
                selectedKey === "shop-reimbursements" ? "#4979A7" : "#4D4D4D",
              fontWeight: "400",
              letterSpacing: "0.5px",
              lineHeight: "1",
              cursor: "pointer",
            }}
          >
            <img
              src="/images/shop-icon.png"
              alt="Shop Reimbursements"
              style={{
                width: "24px",
                height: "24px",
                flexShrink: 0,
              }}
            />
            <div>Shop Reimbursements</div>
          </div>

          {/* Participating Shops */}
          <div
            onClick={() =>
              handleMenuClick("participating-shops", "/participating-shops")
            }
            style={{
              alignItems: "center",
              borderRadius: "4px",
              display: "flex",
              marginTop: "10px",
              minHeight: "44px",
              width: "100%",
              padding: "10px",
              gap: "10px",
              fontSize: "18px",
              color:
                selectedKey === "participating-shops" ? "#4979A7" : "#4D4D4D",
              fontWeight: "400",
              letterSpacing: "0.5px",
              lineHeight: "1",
              justifyContent: "start",
              backgroundColor:
                selectedKey === "participating-shops"
                  ? "rgba(73, 121, 167, 0.1)"
                  : "#FFF",
              cursor: "pointer",
            }}
          >
            <img
              src="/images/participating-shops-icon.png"
              alt="Participating Shops"
              style={{
                width: "24px",
                height: "24px",
                flexShrink: 0,
              }}
            />
            <div>Participating Shops</div>
          </div>

          {/* Reports */}
          <div
            onClick={() => handleMenuClick("reports", "/reports")}
            style={{
              alignItems: "center",
              borderRadius: "4px",
              display: "flex",
              marginTop: "10px",
              minHeight: "44px",
              width: "100%",
              padding: "10px",
              gap: "10px",
              fontSize: "18px",
              color: selectedKey === "reports" ? "#4979A7" : "#4D4D4D",
              fontWeight: "400",
              letterSpacing: "0.5px",
              lineHeight: "1",
              justifyContent: "start",
              backgroundColor:
                selectedKey === "reports" ? "rgba(73, 121, 167, 0.1)" : "#FFF",
              cursor: "pointer",
            }}
          >
            <img
              src="/images/reports-icon.svg"
              alt="Reports"
              style={{
                width: "24px",
                height: "24px",
                flexShrink: 0,
              }}
            />
            <div>Reports</div>
          </div>

          {/* Users */}
          <div
            onClick={() => handleMenuClick("users", "/users")}
            style={{
              alignItems: "center",
              borderRadius: "4px",
              display: "flex",
              marginTop: "10px",
              minHeight: "44px",
              width: "100%",
              padding: "10px",
              gap: "10px",
              fontSize: "18px",
              color: selectedKey === "users" ? "#4979A7" : "#4D4D4D",
              fontWeight: "400",
              letterSpacing: "0.5px",
              lineHeight: "1",
              justifyContent: "start",
              backgroundColor:
                selectedKey === "users" ? "rgba(73, 121, 167, 0.1)" : "#FFF",
              cursor: "pointer",
            }}
          >
            <img
              src="/images/users-icon-new.svg"
              alt="Users"
              style={{
                width: "24px",
                height: "24px",
                flexShrink: 0,
              }}
            />
            <div>Users</div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div
        style={{
          marginTop: "244px",
          fontSize: "18px",
          color: "#4D4D4D",
          fontWeight: "400",
          letterSpacing: "0.5px",
          lineHeight: "1",
        }}
      >
        <div
          style={{
            borderRadius: "4px",
            backgroundColor: "#FFF",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#CCC",
              marginBottom: "10px",
            }}
          />

          {/* FAQs */}
          <div
            onClick={() => handleMenuClick("faqs", "/faqs")}
            style={{
              alignItems: "center",
              borderRadius: "4px",
              display: "flex",
              marginTop: "10px",
              minHeight: "44px",
              width: "100%",
              padding: "10px",
              gap: "10px",
              justifyContent: "start",
              backgroundColor:
                selectedKey === "faqs" ? "rgba(73, 121, 167, 0.1)" : "#FFF",
              cursor: "pointer",
              fontSize: "18px",
              color: selectedKey === "faqs" ? "#4979A7" : "#4D4D4D",
              fontWeight: "400",
              letterSpacing: "0.5px",
              lineHeight: "1",
            }}
          >
            <img
              src="/images/faqs-icon.svg"
              alt="FAQs"
              style={{
                width: "24px",
                height: "24px",
                flexShrink: 0,
              }}
            />
            <div>FAQs</div>
          </div>

          {/* Contact Us */}
          <div
            onClick={() => handleMenuClick("contact", "/contact")}
            style={{
              alignItems: "center",
              borderRadius: "4px",
              display: "flex",
              marginTop: "10px",
              minHeight: "44px",
              width: "100%",
              padding: "10px",
              gap: "10px",
              justifyContent: "start",
              backgroundColor:
                selectedKey === "contact" ? "rgba(73, 121, 167, 0.1)" : "#FFF",
              cursor: "pointer",
              fontSize: "18px",
              color: selectedKey === "contact" ? "#4979A7" : "#4D4D4D",
              fontWeight: "400",
              letterSpacing: "0.5px",
              lineHeight: "1",
            }}
          >
            <img
              src="/images/contact-us-icon.svg"
              alt="Contact Us"
              style={{
                width: "24px",
                height: "24px",
                flexShrink: 0,
              }}
            />
            <div>Contact Us</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        title={null}
        placement="left"
        onClose={onDrawerClose}
        open={!collapsed}
        width={295}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ display: "none" }}
        styles={{
          mask: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Sider
      width={295}
      style={{
        background: "#FFF",
        borderRight: "1px solid #CCC",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
      }}
    >
      {sidebarContent}
    </Sider>
  );
};

export default Sidebar;
